'use client';

import { useState } from 'react';
import Image, { ImageProps } from 'next/image';
import { Box, SxProps, Theme } from '@mui/material';

export interface SquareIconProps extends Omit<ImageProps, 'src' | 'onError' | 'alt'> {
  /** The source URL of the image */
  src?: string | null;
  /** The alternative text for the image */
  alt?: string;
  /** The dimensions (width and height) of the icon square */
  size?: number;
  /** The border radius of the icon square container */
  borderRadius?: string | number;
  /** Custom fallback to render if the image is missing or fails to load */
  fallback?: React.ReactNode;
  /** Additional SX styles for the container */
  sx?: SxProps<Theme>;
}

/**
 * A generic square icon component that handles image loading errors
 * gracefully with a fallback UI.
 */
export function SquareIcon({
  src,
  alt = '',
  size = 44,
  borderRadius = '8px',
  fallback,
  style,
  sx,
  ...props
}: SquareIconProps) {
  const [hasError, setHasError] = useState(false);

  // Default fallback is the theme-aware Porvenix circle icon
  const defaultFallback = (
    <Box
      component="svg"
      width={size * 0.6}
      height={size * 0.6}
      viewBox="0 0 32 32"
      fill="none"
      xmlns="http://www.w3.org/2000/svg"
      sx={{ color: 'text.primary' }}
    >
      <circle
        cx="16"
        cy="16"
        r="14"
        stroke="currentColor"
        strokeWidth="3"
        fill="none"
        strokeLinecap="round"
        strokeLinejoin="round"
      />
      <line
        x1="4"
        y1="16"
        x2="28"
        y2="16"
        stroke="currentColor"
        strokeWidth="3"
        strokeLinecap="round"
      />
    </Box>
  );

  if (!src || hasError) {
    return (
      <Box
        sx={{
          width: size,
          height: size,
          minWidth: size,
          minHeight: size,
          borderRadius: borderRadius,
          bgcolor: 'background.default',
          border: '1px solid',
          borderColor: 'divider',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          flexShrink: 0,
          overflow: 'hidden',
          ...sx,
        }}
      >
        {fallback || defaultFallback}
      </Box>
    );
  }

  return (
    <Box
      sx={{
        position: 'relative',
        width: size,
        height: size,
        minWidth: size,
        minHeight: size,
        borderRadius: borderRadius,
        overflow: 'hidden',
        flexShrink: 0,
        ...sx,
      }}
    >
      <Image
        key={src}
        src={src}
        alt={alt || ''}
        fill
        sizes={`${size}px`}
        style={{
          objectFit: 'cover',
          ...style,
        }}
        onError={() => setHasError(true)}
        {...props}
      />
    </Box>
  );
}
